/**
 * Request Cache Utility
 * Prevents duplicate API calls and provides in-memory caching with TTL
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch data with caching and request deduplication
   * @param key Unique cache key
   * @param fetcher Function that returns a Promise with the data
   * @param ttl Time to live in milliseconds (default: 5 minutes)
   */
  async fetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.defaultTTL
  ): Promise<T> {
    const cached = this.cache.get(key);
    
    // Return cached data if still valid
    if (cached && Date.now() - cached.timestamp < ttl) {
      console.log(`[RequestCache] Cache hit for: ${key} (age: ${Math.round((Date.now() - cached.timestamp) / 1000)}s)`);
      return cached.data;
    }
    
    // If request is already in flight, return the same promise
    // This prevents duplicate API calls when multiple components request the same data
    if (cached?.promise) {
      console.log(`[RequestCache] Request in flight for: ${key}, reusing promise`);
      return cached.promise;
    }
    
    // Make new request
    console.log(`[RequestCache] Fetching fresh data for: ${key}`);
    const promise = fetcher();
    this.cache.set(key, { 
      data: null as any, 
      timestamp: Date.now(), 
      promise 
    });
    
    try {
      const data = await promise;
      this.cache.set(key, { 
        data, 
        timestamp: Date.now() 
      });
      return data;
    } catch (error) {
      // Remove failed request from cache
      this.cache.delete(key);
      throw error;
    }
  }
  
  /**
   * Manually invalidate a cache entry
   */
  invalidate(key: string) {
    console.log(`[RequestCache] Invalidating cache for: ${key}`);
    this.cache.delete(key);
  }
  
  /**
   * Clear all cached entries
   */
  clear() {
    console.log(`[RequestCache] Clearing all cache entries`);
    this.cache.clear();
  }
  
  /**
   * Get cache size for debugging
   */
  size(): number {
    return this.cache.size;
  }
  
  /**
   * Get all cache keys for debugging
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }
}

// Export singleton instance
export const requestCache = new RequestCache();
